import Papa from 'papaparse';
import { TSmartVariableDefinition } from '@/types/TSmartCRM';
import { EFieldType } from 'src/enums/ESmartCRM';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import { CSVRow, CSVRowError, CSVValidationResult } from '@/types/TCSV';

export const generateSampleCSV = (variables: TSmartVariableDefinition[]): string => {
	const requiredFields = variables.filter((v) => v.isRequired);
	const headers = ['name', 'email', 'number', ...requiredFields.map((v) => v.internalKey)];

	const sampleRow = [
		'John Doe',
		'john@example.com',
		'+1-555-123-4567',
		...requiredFields.map((field) => {
			switch (field.fieldType) {
				case EFieldType.TEXT:
					return 'Sample text';
				case EFieldType.NUMBER:
					return '100';
				case EFieldType.BOOLEAN:
					return 'true';
				case EFieldType.DATE:
					return '2024-01-15';
				default:
					return 'Sample value';
			}
		}),
	];

	return [headers.join(','), sampleRow.join(',')].join('\n');
};

export const downloadSampleCSV = (variables: TSmartVariableDefinition[]) => {
	const csvContent = generateSampleCSV(variables);
	const blob = new Blob([csvContent], { type: 'text/csv' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = 'customer_sample.csv';
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
};

export const parseCSV = (file: File): Promise<any[]> => {
	return new Promise((resolve) => {
		Papa.parse(file, {
			header: true,
			skipEmptyLines: true,
			dynamicTyping: false,
			complete: (results) => {
				// Always resolve with data, even if there are parsing errors
				// Filter out completely empty rows
				const cleanData = results.data.filter((row: any) => {
					return Object.values(row).some(
						(value) => value !== null && value !== undefined && value !== '',
					);
				});
				resolve(cleanData);
			},
			error: () => {
				// Even on error, resolve with empty array to continue processing
				resolve([]);
			},
		});
	});
};

const validateField = (value: any, field: TSmartVariableDefinition): CSVRowError[] => {
	const errors: CSVRowError[] = [];

	if (field.isRequired && (value === undefined || value === null || value === '')) {
		errors.push({
			field: field.internalKey,
			message: 'Required',
			value,
		});
		return errors;
	}

	if (value === undefined || value === null || value === '') {
		return errors;
	}

	switch (field.fieldType) {
		case EFieldType.NUMBER: {
			if (isNaN(Number(value))) {
				errors.push({
					field: field.internalKey,
					message: 'Must be number',
					value,
				});
			}
			break;
		}

		case EFieldType.BOOLEAN: {
			const boolValue = String(value).toLowerCase();
			if (!['true', 'false', '1', '0', 'yes', 'no'].includes(boolValue)) {
				errors.push({
					field: field.internalKey,
					message: 'Must be true/false',
					value,
				});
			}
			break;
		}

		case EFieldType.DATE: {
			const date = new Date(value);
			if (isNaN(date.getTime())) {
				errors.push({
					field: field.internalKey,
					message: 'Invalid date',
					value,
				});
			}
			break;
		}
	}

	return errors;
};

export const validateCSVData = (
	data: any[],
	variables: TSmartVariableDefinition[],
): CSVValidationResult => {
	if (!data || data.length === 0) {
		return {
			rows: [],
			validRows: [],
			totalRows: 0,
			errorCount: 0,
			duplicateCount: 0,
		};
	}

	const phoneNumbers = new Set<string>();
	const emailSet = new Set<string>();
	const processedRows: CSVRow[] = [];

	data.forEach((originalRow, _index) => {
		try {
			const row = { ...originalRow };
			const errors: CSVRowError[] = [];
			let isDuplicate = false;
			let duplicateField = '';

			// Basic field validation with error handling
			if (!row.name?.toString().trim()) {
				errors.push({ field: 'name', message: 'Required', value: row.name });
			}

			if (!row.email?.toString().trim()) {
				errors.push({ field: 'email', message: 'Required', value: row.email });
			} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
				errors.push({ field: 'email', message: 'Invalid format', value: row.email });
			} else if (emailSet.has(row.email.toLowerCase())) {
				isDuplicate = true;
				duplicateField = 'email';
			} else {
				emailSet.add(row.email.toLowerCase());
			}

			// Phone validation with error handling
			if (!row.number?.toString().trim()) {
				errors.push({ field: 'number', message: 'Required', value: row.number });
			} else {
				try {
					const phone = parsePhoneNumberWithError(row.number.toString(), 'US');
					const formattedPhone = phone.format('E.164');

					if (phoneNumbers.has(formattedPhone)) {
						isDuplicate = true;
						duplicateField = 'number';
					} else {
						phoneNumbers.add(formattedPhone);
						row.number = formattedPhone;
					}
				} catch (_error) {
					errors.push({ field: 'number', message: 'Invalid format', value: row.number });
				}
			}

			// Dynamic field validation with error handling
			variables.forEach((field) => {
				try {
					const fieldErrors = validateField(row[field.internalKey], field);
					errors.push(...fieldErrors);

					if (
						fieldErrors.length === 0 &&
						row[field.internalKey] !== undefined &&
						row[field.internalKey] !== null &&
						row[field.internalKey] !== ''
					) {
						switch (field.fieldType) {
							case EFieldType.NUMBER: {
								row[field.internalKey] = Number(row[field.internalKey]);
								break;
							}
							case EFieldType.BOOLEAN: {
								const boolValue = String(row[field.internalKey]).toLowerCase();
								row[field.internalKey] = ['true', '1', 'yes'].includes(boolValue);
								break;
							}
						}
					}
				} catch (_error) {
					// If field validation fails, add generic error and continue
					errors.push({
						field: field.internalKey,
						message: 'Validation error',
						value: row[field.internalKey],
					});
				}
			});

			processedRows.push({
				data: row,
				errors,
				isDuplicate,
				duplicateField,
				isValid: errors.length === 0 && !isDuplicate,
			});
		} catch (_error) {
			// If entire row processing fails, create error row
			processedRows.push({
				data: originalRow,
				errors: [{ field: 'row', message: 'Processing error', value: '' }],
				isDuplicate: false,
				duplicateField: '',
				isValid: false,
			});
		}
	});

	const validRows = processedRows.filter((row) => row.isValid).map((row) => row.data);

	return {
		rows: processedRows,
		validRows,
		totalRows: data.length,
		errorCount: processedRows.filter((row) => row.errors.length > 0).length,
		duplicateCount: processedRows.filter((row) => row.isDuplicate).length,
	};
};
